
import { computed, defineComponent, PropType, reactive } from 'vue'

// @components
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// @types
import { Courier } from '@/new-services/types/couriers'

// @realisations
import { users } from '@/core/realisations/users.realisation'

// @services
import couriersAPI from '@/new-services/couriers.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

export default defineComponent({
    name: 'couriers-table-row-actions',
    components: { ButtonDropdown, TheButton },
    props: {
        courier: {
            type: Object as PropType<Courier>,
            required: true,
        },
        canUpdate: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update', 'set-phone', 'routing', 'qr'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { havePermissions } = useAccount()

        const state = reactive({
            is_user_session_loading: false,
            is_code_loading: false,
            is_create_route_loading: false,
            is_routing_loading: false,
        })

        const createUserSession = async () => {
            try {
                state.is_user_session_loading = true
                const response = await users.getUserToken(props.courier.userId)

                const url = process.env.VUE_APP_IMPERSONATE_COURIER_URL!
                window.open(`${url}?impersonate_token=${response.access_token}`, '_blank')?.focus()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_user_session_loading = false
            }
        }

        const getCode = async () => {
            try {
                state.is_code_loading = true
                const response = await couriersAPI.getCode(props.courier.phoneNumber)

                if (response.data.data) {
                    notifications.notify({
                        title: 'Код успешно получен',
                        description: `Код для входа: ${response.data.data.code}`,
                        type: 'success',
                    })
                } else if (response.data.message) {
                    notifications.notify({
                        title: 'Произошла ошибка',
                        description: response.data.message,
                        type: 'danger',
                    })
                }
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_code_loading = false
            }
        }

        const emitUpdate = () => emit('update', { ...props.courier })
        const emitSetPhone = () => emit('set-phone', props.courier.id)
        const emitQr = () => emit('qr', props.courier.id)

        const createRoute = async () => {
            if (state.is_create_route_loading) return

            const answer = confirm(
                `Вы уверены что хотите построить маршрут для курьера ${props.courier.fullName} ?`
            )
            if (!answer) return

            try {
                state.is_create_route_loading = true

                await couriersAPI.createRoute(props.courier.id)

                notifications.notify({
                    type: 'success',
                    title: props.courier.fullName,
                    description: 'Маршрут построен',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При построении маршрута произошла ошибка',
                })
            } finally {
                state.is_create_route_loading = false
            }
        }

        const routingNotification = computed(() =>
            props.courier.routingEnabled
                ? 'Маршрутизация успешно выключена'
                : 'Маршрутизация успешно включена'
        )

        const manageRoute = async () => {
            if (state.is_routing_loading) return

            try {
                state.is_routing_loading = true

                await couriersAPI.manageRouting(
                    props.courier.id,
                    Number(!props.courier.routingEnabled)
                )

                emit('routing')
                notifications.notify({
                    type: 'success',
                    title: props.courier.fullName,
                    description: routingNotification.value,
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_routing_loading = false
            }
        }

        const getRouteText = computed(() =>
            props.courier.routingEnabled ? 'Выключить маршрутизацию' : 'Включить маршрутизацию'
        )

        return {
            state,
            //
            createUserSession,
            havePermissions,
            emitUpdate,
            emitSetPhone,
            emitQr,
            getCode,
            createRoute,
            getRouteText,
            manageRoute,
        }
    },
})
